// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — Compare Page
// Side-by-side comparison of all scenarios in a selected project
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { GitCompare, Trophy, TrendingDown, TrendingUp, Star } from 'lucide-react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';

import { Card, CardHeader, CardBody, EmptyState, Badge, Select, Spinner, StatRow } from '@/components/ui/index';
import { useProjects, useCompare, useScenarios } from '@/hooks/useQueries';
import { fmtShort, fmt, fmtPercent, CHART_COLORS } from '@/lib/utils';

const PALETTE = [CHART_COLORS.navy, CHART_COLORS.amber, CHART_COLORS.teal, CHART_COLORS.red, CHART_COLORS.purple];
const tooltipStyle = { background: '#0c1b2e', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 };

export function Compare() {
  const [selectedProject, setSelectedProject] = useState<string>('');
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const { data: comparison, isLoading: compLoading } = useCompare(selectedProject || null);
  const { data: scenarios, isLoading: scenLoading } = useScenarios(selectedProject || null);

  const isLoading = compLoading || scenLoading;

  // Build bar chart data from scenarios
  const barData = comparison?.scenarios.map((s, i) => ({
    name: s.name.length > 18 ? s.name.slice(0, 17) + '…' : s.name,
    fullName: s.name,
    lcc: Math.round(s.totalLCC),
    uac: Math.round(s.uac),
    color: PALETTE[i % PALETTE.length] ?? CHART_COLORS.navy,
    rank: s.rank,
    isBaseline: s.isBaseline,
    vsBaseline: s.vsBaseline,
  })) ?? [];

  // Radar data — normalise costs to 0-100 scale per category across scenarios
  const allScenarios = scenarios ?? [];
  const radarData = (() => {
    if (allScenarios.length < 2) return [];
    const cats = ['initialCostPW', 'omPW', 'energyPW', 'replacementPW'] as const;
    const catLabels: Record<string, string> = {
      initialCostPW: 'Initial', omPW: 'O&M', energyPW: 'Energy', replacementPW: 'Replacement',
    };
    return cats.map(cat => {
      const row: Record<string, string | number> = { subject: catLabels[cat] };
      allScenarios.forEach(s => {
        if (s.results) row[s.name] = Math.round((s.results as unknown as Record<string, number>)[cat] ?? 0);
      });
      return row;
    });
  })();

  return (
    <div className="flex flex-col gap-6">

      {/* Header */}
      <div>
        <h2 className="font-display font-bold text-navy-900 text-2xl">Scenario Comparison</h2>
        <p className="text-navy-500 text-sm mt-1">Compare multiple design alternatives side-by-side to identify the most cost-effective option.</p>
      </div>

      {/* Project selector */}
      <Card>
        <CardBody>
          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex-1 min-w-52">
              {projectsLoading ? (
                <div className="flex gap-2 items-center text-navy-400"><Spinner size={16} /><span className="text-sm">Loading projects…</span></div>
              ) : (
                <Select
                  label="Select Project"
                  options={[
                    { value: '', label: 'Choose a project to compare…' },
                    ...(projects ?? []).map(p => ({ value: p.id, label: p.name })),
                  ]}
                  value={selectedProject}
                  onChange={e => setSelectedProject(e.target.value)}
                />
              )}
            </div>
            {selectedProject && comparison && (
              <div className="flex items-center gap-3 text-sm text-navy-600">
                <Badge variant="navy">{comparison.scenarios.length} scenarios</Badge>
                {comparison.savings > 0 && (
                  <Badge variant="teal">
                    Max savings: {fmtShort(comparison.savings)}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Placeholder */}
      {!selectedProject && (
        <EmptyState
          icon={<GitCompare size={32} />}
          title="Select a project to compare"
          description="Choose a project from the dropdown above to see side-by-side LCC comparison of all saved scenarios."
        />
      )}

      {selectedProject && isLoading && (
        <div className="flex justify-center py-20 gap-3 text-navy-400">
          <Spinner size={24} /> <span className="font-medium">Loading comparison…</span>
        </div>
      )}

      {selectedProject && !isLoading && comparison && (

        <>
          {/* Winner banner */}
          {comparison.winner && comparison.scenarios.length > 1 && (
            <div className="bg-gradient-to-r from-teal-700 to-teal-500 rounded-xl px-6 py-4 text-white flex items-center gap-4 shadow-teal">
              <Trophy size={28} className="flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-widest text-teal-200">Most Cost-Effective Option</p>
                <p className="font-display font-bold text-xl mt-0.5">
                  {comparison.scenarios.find(s => s.id === comparison.winner)?.name}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-teal-200">Total LCC</p>
                <p className="font-mono font-bold text-2xl">
                  {fmtShort(comparison.scenarios.find(s => s.id === comparison.winner)?.totalLCC ?? 0)}
                </p>
              </div>
            </div>
          )}

          {comparison.scenarios.length === 0 ? (
            <EmptyState
              icon={<GitCompare size={28} />}
              title="No scenarios to compare"
              description="Save at least two scenarios in this project to enable comparison."
            />
          ) : (

            <>
              {/* Ranking cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {comparison.scenarios.map((s, i) => {
                  const isWinner = s.id === comparison.winner;
                  const isBetter = s.vsBaseline < 0;
                  return (
                    <Card
                      key={s.id}
                      className={`relative overflow-hidden transition-all duration-200 ${isWinner ? 'ring-2 ring-teal-400 ring-offset-1' : ''}`}
                    >
                      <div className="h-1 rounded-t-xl" style={{ background: PALETTE[i % PALETTE.length] }} />
                      <CardBody>
                        <div className="flex items-start justify-between gap-2 mb-4">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-white font-display font-bold text-sm flex-shrink-0"
                              style={{ background: PALETTE[i % PALETTE.length] }}
                            >
                              {s.rank}
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                {s.isBaseline && <Star size={12} className="text-amber-500 fill-amber-500" />}
                                <span className="font-display font-bold text-navy-900 text-sm">{s.name}</span>
                              </div>
                              {s.isBaseline && <span className="text-[10px] text-amber-600 font-semibold">Baseline</span>}
                            </div>
                          </div>
                          {isWinner && <Badge variant="teal">✓ Best</Badge>}
                        </div>

                        <div className="mb-4">
                          <p className="text-[10px] font-display font-semibold uppercase tracking-widest text-navy-400 mb-1">Total LCC</p>
                          <p className="font-mono font-bold text-navy-900 text-2xl">{fmtShort(s.totalLCC)}</p>
                        </div>

                        <div className="bg-parchment-50 rounded-lg p-3 border border-parchment-200">
                          <StatRow label="Annual Cost (UAC)"  value={fmtShort(s.uac)} />
                          {!s.isBaseline && (
                            <StatRow
                              label="vs. Baseline"
                              value={
                                <span className={isBetter ? 'text-teal-600 font-bold' : 'text-red-600 font-bold'}>
                                  <span className="inline-flex items-center gap-1">
                                    {isBetter
                                      ? <TrendingDown size={12} />
                                      : <TrendingUp size={12} />
                                    }
                                    {isBetter ? '' : '+'}{fmtPercent(s.vsBaseline)}
                                  </span>
                                </span>
                              }
                            />
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  );
                })}
              </div>

              {/* Bar chart comparison */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <div className="font-display font-bold text-navy-900 text-sm">Total LCC Comparison</div>
                  </CardHeader>
                  <CardBody>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd7" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} tickFormatter={v => fmtShort(v)} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fontFamily: 'Nunito', fontWeight: 600 }} width={110} />
                        <RTooltip
                          contentStyle={tooltipStyle}
                          formatter={(v: number) => [fmt(v), 'Total LCC']}
                        />
                        <Bar dataKey="lcc" radius={[0, 4, 4, 0]}>
                          {barData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} fillOpacity={entry.rank === 1 ? 1 : 0.7} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <div className="font-display font-bold text-navy-900 text-sm">Uniform Annual Cost (UAC)</div>
                  </CardHeader>
                  <CardBody>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd7" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} tickFormatter={v => fmtShort(v)} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fontFamily: 'Nunito', fontWeight: 600 }} width={110} />
                        <RTooltip contentStyle={tooltipStyle} formatter={(v: number) => [fmt(v), 'UAC']} />
                        <Bar dataKey="uac" radius={[0, 4, 4, 0]}>
                          {barData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} fillOpacity={0.75} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardBody>
                </Card>
              </div>

              {/* Radar chart (only if 2+ scenarios with results) */}
              {allScenarios.length >= 2 && radarData.length > 0 && (
                <Card>
                  <CardHeader>
                    <div>
                      <div className="font-display font-bold text-navy-900 text-sm">Cost Profile Radar</div>
                      <div className="text-xs text-navy-400 mt-0.5">Present-worth by cost category across scenarios</div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <ResponsiveContainer width="100%" height={320}>
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="#e2ddd7" />
                        <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12, fontFamily: 'Nunito', fontWeight: 600 }} />
                        {allScenarios.filter(s => s.results).map((s, i) => (
                          <Radar
                            key={s.id}
                            name={s.name}
                            dataKey={s.name}
                            stroke={PALETTE[i % PALETTE.length]}
                            fill={PALETTE[i % PALETTE.length]}
                            fillOpacity={0.1}
                            strokeWidth={2}
                          />
                        ))}
                        <Legend wrapperStyle={{ fontSize: 12, fontFamily: 'Nunito' }} />
                        <RTooltip contentStyle={tooltipStyle} formatter={(v: number) => [fmt(v), '']} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardBody>
                </Card>
              )}

              {/* Detailed comparison table */}
              <Card>
                <CardHeader>
                  <div className="font-display font-bold text-navy-900 text-sm">
                    Detailed Side-by-Side Comparison
                  </div>
                </CardHeader>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="!text-left">Metric</th>
                        {comparison.scenarios.map((s, i) => (
                          <th key={s.id} style={{ color: PALETTE[i % PALETTE.length] }}>
                            {s.name}
                            {s.isBaseline && ' ★'}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: 'Rank',         fn: (s: typeof comparison.scenarios[0]) => `#${s.rank}` },
                        { label: 'Total LCC',    fn: (s: typeof comparison.scenarios[0]) => fmt(s.totalLCC) },
                        { label: 'Annual UAC',   fn: (s: typeof comparison.scenarios[0]) => fmt(s.uac) },
                        { label: 'vs. Baseline', fn: (s: typeof comparison.scenarios[0]) => s.isBaseline ? '—' : `${s.vsBaseline > 0 ? '+' : ''}${fmtPercent(s.vsBaseline)}` },
                      ].map(row => (
                        <tr key={row.label}>
                          <td>{row.label}</td>
                          {comparison.scenarios.map(s => (
                            <td key={s.id}>{row.fn(s)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
